import React from 'react'
import { connect, useDispatch } from 'react-redux'

import { removeShortlist } from '@store/actionSlices/shortlist'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import { Modal } from '@components/modals'

import { Pagination, ProjectListDetails } from '@api/project-list'

import Throttle from '@utilities/throttle'
import useDefaultRoom from '@utilities/use-default-room'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { ArrowRefreshSvg, ChevronUpDownSvg } from '@svg/react'

import { extractRoleFromToken } from '@adUtilities/token-helper'
import { Role } from '@adUtilities/types'

import constants from './constants'

function ProjectSwitchHandler({
  projectIdentity,
  projectList,
  pagination,
  isActive,
  setPage,
  setProjectQuery,
  activeRouteFirebase,
}: {
  projectIdentity: ProjectIdentity
  projectList: Array<ProjectListDetails>
  pagination: Pagination
  isActive: boolean
  setPage: (status: 'prev' | 'next') => void
  setProjectQuery: (query: string) => void
  activeRouteFirebase?: string
}) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const dispatch = useDispatch()

  const userRole = extractRoleFromToken()
  const canAccessProjectSearch =
    Role.SUPER_ADMIN === userRole || Role.ADMIN === userRole

  const [isModalActive, modalToggle] = React.useState(false)

  const [canonicalName, projectCanonicalName] = React.useState('')

  const query = Throttle(canonicalName, 1000)

  const triggerFirebaseProjectSwitch = async ({
    projectId,
    projectLabel,
    projectName,
  }: {
    projectId: string
    projectLabel: string
    projectName: string
  }) => {
    await firebaseControlQuery.update({
      [`projectSwitch`]: {
        projectId,
        projectLabel,
        projectName,
      },
    })
  }

  const getProject = (prjct: ProjectListDetails) => {
    const newSessionList = projectIdentity.sessionList.map((session) => {
      if (session.projectId === prjct.canonicalName) {
        return {
          ...session,
          projectName: session.projectName || prjct.name,
          projectUUID: session.projectUUID || prjct.uuid,
        }
      }
      return session
    })
    const project = newSessionList.find(
      (session) => session.projectId === prjct.canonicalName
    )
    return { project, newSessionList }
  }

  const { getDefaultRoom, setDefaultRoom } = useDefaultRoom()

  const projectSwitch = async (prjct: ProjectListDetails) => {
    const { project, newSessionList } = getProject(prjct)
    if (!project) {
      alert('There are no sessions for the selected project.')
      return
    }
    const projectName = project.projectId || prjct.canonicalName
    const defaultRoom = await getDefaultRoom({
      projectName,
      prioritizeCurrentRoom: true,
    })
    if (defaultRoom) {
      setDefaultRoom(defaultRoom)
    }

    await triggerFirebaseProjectSwitch({
      projectId: project.projectUUID || prjct.uuid,
      projectName,
      projectLabel: project.projectName || prjct.name,
    })
    localStorage.setItem(
      'projectIdentity',
      JSON.stringify({
        ...projectIdentity,
        sessionList: newSessionList,
        projectId: project.projectId,
        projectName: project.projectId,
        sessionId: project.sessionId || prjct.canonicalName,
        projectUUID: project.projectUUID || prjct.uuid,
        projectLabel: project.projectName || prjct.name,
      })
    )
    dispatch(removeShortlist())
    window.location.href = `/${activeRouteFirebase}`
    modalToggle(false)
  }

  React.useEffect(() => {
    setProjectQuery(query)
  }, [query])

  React.useEffect(() => {
    projectCanonicalName('')
    setProjectQuery('')
  }, [isModalActive])

  return (
    <>
      <Modal
        toggleModal={(res) => modalToggle(res)}
        isVisible={isModalActive}
        modalWidth="max-w-lg"
        title="Project Switch"
        noMainControls
        hasHeaderControl
        theme={{
          textColor: 'text-mainColour',
          mainBg: 'bg-mainColour',
          secondaryBg: 'bg-neutralColour',
        }}
      >
        <div>
          <div
            className={`my-4 h-auto w-full transition-opacity ${
              isActive ? 'bg-secondaryColour' : 'bg-gray-200'
            }`}
          >
            {canAccessProjectSearch && (
              <div>
                <input
                  type="text"
                  name="sessionName"
                  value={canonicalName}
                  onChange={(e) => projectCanonicalName(e.target.value)}
                  className="border-1 my-2 w-full rounded border-gray-500 text-2xl focus:border-gray-500 focus:ring-0"
                />
              </div>
            )}

            {projectList.map((project) => (
              <button
                key={`${project.id}-project-switch`}
                disabled={!isActive}
                onClick={() => projectSwitch(project)}
                type="button"
                className="flex h-auto w-full items-center border-t-2 px-2 py-4 text-2xl text-mainColour"
              >
                {project.name}
                <ArrowRefreshSvg
                  className={`ml-auto ${
                    !isActive ? 'text-gray-500' : 'text-sky-500'
                  }`}
                />
              </button>
            ))}
          </div>
          {pagination.total > constants.PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <div>
                Page <b>{pagination.currentPage}</b> of{' '}
                <b>{pagination.lastPage}</b>
              </div>

              {!isActive && (
                <p className="inline-flex items-center gap-2 text-gray-500">
                  <ArrowRefreshSvg className="animate-spin text-sky-500" />{' '}
                  Loading...
                </p>
              )}

              <div
                className={`flex items-center gap-4 ${
                  !isActive ? 'text-gray-500' : ''
                }`}
              >
                <button
                  disabled={!isActive}
                  type="button"
                  onClick={() => setPage('prev')}
                  className="font-medium"
                >
                  Prev
                </button>
                |
                <button
                  disabled={!isActive}
                  type="button"
                  onClick={() => setPage('next')}
                  className="font-medium"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <button
        disabled={!isActive}
        type="button"
        onClick={() => modalToggle(true)}
        className={`ml-auto flex w-auto cursor-pointer items-center gap-1 rounded-lg text-mainColour ${
          isActive ? 'bg-neutralColour' : 'bg-gray-200'
        } px-4 py-2.5 text-2xl`}
      >
        {projectIdentity.projectLabel}
        <ChevronUpDownSvg width="35" height="35" />
      </button>
    </>
  )
}

export default connect(({ firestore: { session } }: RootStateTypeExtra) => ({
  activeRouteFirebase: session?.activeRoute,
}))(ProjectSwitchHandler)
